import { call, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import uniqid from 'uniqid';

import { getProductScope } from '@discover-frontend/utils';
import {
  CHECK_DATA_CONVERSATION_FAILURE,
  CHECK_DATA_CONVERSATION_REQUEST,
  CHECK_DATA_CONVERSATION_SUCCESS,
  CREATE_CONVERSATION_FAILURE,
  CREATE_CONVERSATION_REQUEST,
  CREATE_CONVERSATION_SUCCESS,
  GET_CONVERSATIONS_RECENT_FAILURE,
  GET_CONVERSATIONS_RECENT_REQUEST,
  GET_CONVERSATIONS_RECENT_SUCCESS,
  HIDE_CREATE_AUDIENCE_MODAL,
  CLEAR_CONVERSATION_QUERY_REQUEST,
  CONVERSATION_STATUS_REQUEST,
  GET_REPOSITORY_SUCCESS,
  CLEAR_ALL_QUERIES_REQUEST,
} from '../constants/action-types';
import * as api from '../services/engineApi';
import translations from '../lang';
import { addToast } from '../actions';
import { prepareNewConversation } from '../operations/repository';
import * as engineAffinityApi from '../services/engineAffinityApi';
import * as repositoryApi from '../services/repositoryApi';
import sagaFetch from './SagaFetch';

function* fetchRecentConversations() {
  const { response, error } = yield call(api.fetchRecentConversationsApi);
  if (!error) {
    const items = response.data.map((item) => {
      const entityId = uniqid();
      return {
        id: entityId,
        query: item,
      };
    });
    return yield put({
      type: GET_CONVERSATIONS_RECENT_SUCCESS,
      response: items,
    });
  }

  return yield put({ type: GET_CONVERSATIONS_RECENT_FAILURE, message: error });
}

function* fetchAndUpdateConversations(newAudience) {
  const { response, error } = yield sagaFetch(
    repositoryApi.fetchRepositoryApi,
    {
      mode: 'conversation',
    }
  );

  if (!error) {
    const currentData = yield select((state) => state.repository.data);
    yield put({
      type: GET_REPOSITORY_SUCCESS,
      response: {
        ...currentData,
        conversation: [
          // adding the new audience on top
          newAudience.data,
          // and removing it from the list if already present
          ...response.data.filter((d) => d.id !== newAudience.data.id),
        ],
      },
      total_count: response.total_count + 1,
    });
  }
}

function* fetchCreateConversation(action) {
  const { data } = action;

  // 1. create the conversation
  const conversation = prepareNewConversation(data);
  conversation.scope = getProductScope();

  const { response: checkDataResponse } = yield sagaFetch(
    engineAffinityApi.fetchConversationCheckApi,
    conversation
  );

  if (checkDataResponse && checkDataResponse.data.valid) {
    yield put({
      type: CHECK_DATA_CONVERSATION_SUCCESS,
      message: translations['en-US'].QUERY_BUILDER.checkDataSuccess,
      mentions: checkDataResponse.data.mention,
    });

    const { response: conversationResponse, error: conversationError } =
      yield sagaFetch(
        engineAffinityApi.fetchCreateConversationApi,
        conversation
      );

    let error = conversationError;

    if (!error) {
      // 2. create the audience
      const { response: audienceResponse, error: audienceError } =
        yield sagaFetch(repositoryApi.fetchCreateAudienceApi, {
          ...conversation,
          target_membership_id: conversationResponse.data.membership_id,
        });

      error = audienceError;

      if (!error) {
        yield put({
          type: HIDE_CREATE_AUDIENCE_MODAL,
        });

        yield put(
          addToast({
            status: 'success',
            message: translations['en-US'].TOASTS.CREATE_CONVERSATION,
          })
        );

        // 3. change repo folder
        yield call(fetchAndUpdateConversations, audienceResponse);

        // 4. open web socket
        yield put({
          type: CONVERSATION_STATUS_REQUEST,
          membershipId: conversationResponse.data.membership_id,
          audienceId: audienceResponse.data.id,
        });

        const recentValues = Object.values(data.conversationQueries) || [];
        // 5. post recent conversations
        yield call(api.fetchPostRecentConversationsApi, {
          recent: recentValues.filter((item) => item),
          scope: getProductScope(),
        });

        // 6. cleanup
        yield put({
          type: CLEAR_ALL_QUERIES_REQUEST,
        });

        yield put({
          type: CLEAR_CONVERSATION_QUERY_REQUEST,
        });

        // 7. success
        return yield put({
          type: CREATE_CONVERSATION_SUCCESS,
          response: conversationResponse.data,
        });
      }
    }

    yield put(
      addToast({
        status: 'error',
        message: translations['en-US'].TOASTS.CREATE_CONVERSATION_ERROR,
      })
    );

    return yield put({
      type: CREATE_CONVERSATION_FAILURE,
      message: error.message,
    });
  }
  return yield put({
    type: CHECK_DATA_CONVERSATION_FAILURE,
    error: true,
    message: translations['en-US'].QUERY_BUILDER.checkDataFailure,
  });
}

function* handleCheckDataConversation(action) {
  const { data } = action;
  const conversation = prepareNewConversation(data);
  conversation.scope = getProductScope();
  const { response } = yield sagaFetch(
    engineAffinityApi.fetchConversationCheckApi,
    conversation
  );

  if (response && response.data.valid) {
    return yield put({
      type: CHECK_DATA_CONVERSATION_SUCCESS,
      message: translations['en-US'].QUERY_BUILDER.checkDataSuccess,
      mentions: response.data.mention,
    });
  }
  return yield put({
    type: CHECK_DATA_CONVERSATION_FAILURE,
    error: true,
    message: translations['en-US'].QUERY_BUILDER.checkDataFailure,
  });
}

export function* conversationActionWatcher() {
  yield takeLatest(GET_CONVERSATIONS_RECENT_REQUEST, fetchRecentConversations);
  yield takeLatest(CREATE_CONVERSATION_REQUEST, fetchCreateConversation);
  yield takeEvery(CHECK_DATA_CONVERSATION_REQUEST, handleCheckDataConversation);
}
